import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import {
  useGuidanceRequestTemplate,
  useSaveGuidanceRequestTemplate,
} from "@/hooks/use-app-settings";
import { GUIDANCE_TEMPLATE_VARIABLES } from "@/lib/guidance-template";

const schema = z.object({
  template: z.string().min(1, "Template body is required"),
});

type FormValues = z.infer<typeof schema>;

export function GuidanceRequestTemplateSection() {
  const { data, isLoading, isError, refetch } = useGuidanceRequestTemplate();
  const save = useSaveGuidanceRequestTemplate();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { template: "" },
  });

  useEffect(() => {
    if (data) {
      form.reset({ template: data.template });
    }
  }, [data, form]);

  if (isError) {
    return (
      <ErrorState message="Failed to load guidance request template" onRetry={() => refetch()} />
    );
  }

  async function onSubmit(values: FormValues) {
    await save.mutateAsync({ template: values.template });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Guidance Request Template</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-3">
          This text is pre-filled into the guidance request text box on the Projects page. It stays
          editable there — this is just the starting point.
        </p>
        <div className="mb-6 rounded-md border border-border bg-muted/30 p-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Use <code className="font-mono">{"{{variable_name}}"}</code> in the body to substitute:
          </p>
          <ul className="space-y-1">
            {GUIDANCE_TEMPLATE_VARIABLES.map((v) => (
              <li key={v.key} className="text-xs text-muted-foreground">
                <code className="font-mono text-foreground">{`{{${v.key}}}`}</code> — {v.label}:{" "}
                {v.description}
              </li>
            ))}
          </ul>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="template"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Template Body</FormLabel>
                  <FormControl>
                    <Textarea
                      className="resize-none font-mono text-sm"
                      rows={8}
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Any placeholder not yet resolvable (e.g. no auditor picked yet) is left as-is in
                    the pop-up until the user fills it in.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end">
              <Button type="submit" disabled={save.isPending || isLoading}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
